import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AppState, Keyboard, Platform } from 'react-native';
import { doc, updateDoc } from 'firebase/firestore';
import { auth, firestore } from '../firebase';
import { Colors } from '../theme';
import { useI18n } from '../i18n';
import type { TonyteUser } from '../types';
import TonyteModal from './TonyteModal';
import FeedbackSurveyModal from './FeedbackSurveyModal';
import { navigationRef } from '../navigation/notificationNav';
import { CALM_MAIN_TABS, nudgeSession } from '../utils/nudgeSession';
import { hasRealProfilePhoto } from '../utils/profilePhoto';
import { getInstalledAppVersion } from '../utils/appVersion';
import { requestNativeStoreReview } from '../utils/storeReview';
import {
  NUDGE_RULES,
  activeDayPatch,
  firstUseMillis,
  observedUpdatePatch,
  pickStartupNudge,
  popupShownPatch,
  profileDismissPatch,
  reviewRequestDecision,
  surveyDecision,
  surveyRemindLaterPatch,
  type NudgeState,
  type ProfileFacts,
  type StartupNudge,
  type SurveyConfig,
} from '../utils/optionalNudges';
import { checkInstalledVersion, isNativePlatform, loadSurveyConfig, patchNudgeState, subscribeNudgeState } from '../services/optionalNudges';

// Mounted once at the navigation root (next to AppUpdatePrompt). After the
// main app is ready it waits for a calm moment — Main route, a calm tab
// (Map/Discover/Feed/Profile), no keyboard, no overlay such as the update
// prompt, app in the foreground, for NUDGE_RULES.calmDwellMs straight — then
// shows at most ONE optional nudge for the session (optionalNudges.ts
// pickStartupNudge). Also opens the survey on request from Settings.

export const VARIANT_COUNTS = { photo: 3, verificationStart: 2, verificationRetry: 1, survey: 2 };
const POLL_MS = 2000;
const NON_MEMBER_ROLES = new Set(['admin', 'co-admin', 'organizer', 'venue', 'partner']);

const toMillis = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? null : parsed;
  }
  return null;
};

const localDayKey = (now: number) => {
  const date = new Date(now);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

type Props = { enabled: boolean; userId: string | null; profile: TonyteUser | null };

const OptionalNudgeHost: React.FC<Props> = ({ enabled, userId, profile }) => {
  const { t } = useI18n();
  const [state, setState] = useState<NudgeState | null>(null);
  const [config, setConfig] = useState<SurveyConfig | undefined>(undefined);
  const [nudge, setNudge] = useState<StartupNudge | null>(null);
  const [surveyOpen, setSurveyOpen] = useState<{ manual: boolean; preview?: boolean } | null>(null);
  const stateRef = useRef<NudgeState | null>(null);
  stateRef.current = state;
  const sessionSetupDone = useRef(false);
  const calmSince = useRef<number | null>(null);

  // Photo/Vibe Verified reminders are for member accounts (same as
  // functions/profileReminders.js); partner/venue/organizer/admin accounts skip them.
  const facts = useMemo<ProfileFacts | null>(() => (profile && !NON_MEMBER_ROLES.has(String(profile.role || '')) ? {
    hasPhoto: hasRealProfilePhoto(profile as never),
    vibeVerified: profile.isVibeVerified === true,
    verificationStatus: profile.faceVerificationStatus,
  } : null), [profile]);

  const firstUse = useMemo(() => firstUseMillis({
    authCreatedAt: toMillis(auth.currentUser?.metadata?.creationTime),
    profileCreatedAt: toMillis((profile as { createdAt?: unknown } | null)?.createdAt),
    state,
  }, Date.now()), [profile, state]);

  useEffect(() => {
    if (!enabled || !userId) {
      setState(null);
      return undefined;
    }
    void loadSurveyConfig().then(setConfig);
    return subscribeNudgeState(userId, setState);
  }, [enabled, userId]);

  // Once per session: count today as an active day (and start first-use
  // tracking), and — if an outdated build paused the survey — check whether
  // the update has since been installed (7-day clock starts only now).
  useEffect(() => {
    if (!enabled || !userId || !state || sessionSetupDone.current || config === undefined) return;
    sessionSetupDone.current = true;
    const now = Date.now();
    const patch = activeDayPatch(state, localDayKey(now), now);
    if (patch) void patchNudgeState(userId, patch).catch(() => undefined);
    // Lets the server pick the evening push line for the user's local day
    // (Europe/Berlin when unknown). Written only when it changes.
    try {
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (typeof timeZone === 'string' && timeZone && (profile as { timeZone?: string } | null)?.timeZone !== timeZone) {
        void updateDoc(doc(firestore, 'users', userId), { timeZone }).catch(() => undefined);
      }
    } catch {
      // Intl unavailable — the server falls back to Europe/Berlin.
    }
    const surveyId = config?.surveyId;
    const survey = surveyId ? state.surveys?.[surveyId] : undefined;
    if (surveyId && survey?.pendingUpdateTarget && isNativePlatform()) {
      void checkInstalledVersion().then(check => {
        const observed = observedUpdatePatch(survey, check, Date.now());
        if (observed) void patchNudgeState(userId, { surveys: { [surveyId]: observed } }).catch(() => undefined);
      });
    }
  }, [config, enabled, profile, state, userId]);

  // Settings → "Give feedback".
  useEffect(() => nudgeSession.onOpenSurveyRequested(({ preview }) => {
    nudgeSession.markSessionUsed();
    setNudge(null);
    setSurveyOpen({ manual: true, preview: __DEV__ && preview === true });
  }), []);

  useEffect(() => {
    nudgeSession.setOverlay('optionalNudge', Boolean(nudge && nudge.kind !== 'review'));
    nudgeSession.setOverlay('feedbackSurvey', Boolean(surveyOpen));
  }, [nudge, surveyOpen]);

  const decideAndShow = useCallback(async () => {
    const current = stateRef.current;
    if (!userId || !current || config === undefined) return;
    const now = Date.now();
    const surveyId = config?.surveyId || '';
    const survey = surveyDecision({ config: config ?? null, survey: current.surveys?.[surveyId], firstUse, now, native: isNativePlatform() });
    const installed = isNativePlatform() ? await getInstalledAppVersion().catch(() => null) : null;
    const review = reviewRequestDecision({ config: config ?? null, state: current, firstUse, now, platform: Platform.OS, appVersion: installed?.versionName || null });
    const { nudge: picked } = pickStartupNudge({
      now,
      sessionUsed: nudgeSession.isSessionUsed(),
      openedFromReminderPush: nudgeSession.wasOpenedFromReminderPush(),
      state: current,
      facts,
      firstUse,
      survey,
      review,
      variants: VARIANT_COUNTS,
    });
    if (!picked) return;
    nudgeSession.markSessionUsed();
    void patchNudgeState(userId, popupShownPatch(picked.kind, current, now, surveyId)).catch(() => undefined);
    if (picked.kind === 'review') {
      // Official API only, no custom UI; the OS decides whether to show anything.
      void requestNativeStoreReview().then(() => patchNudgeState(userId, {
        review: { requests: (current.review?.requests || 0) + 1, lastRequestedAt: now, lastRequestedVersion: installed?.versionName || null },
      })).catch(() => undefined);
      return;
    }
    setNudge(picked);
  }, [config, facts, firstUse, userId]);

  // Calm-moment detector.
  useEffect(() => {
    if (!enabled || !userId) return undefined;
    const timer = setInterval(() => {
      const calm = AppState.currentState === 'active'
        && navigationRef.isReady()
        && navigationRef.getCurrentRoute()?.name === 'Main'
        && CALM_MAIN_TABS.has(nudgeSession.getActiveMainTab() || '')
        && !nudgeSession.hasOverlay()
        && !Keyboard.isVisible()
        && !nudgeSession.isSessionUsed();
      if (!calm) {
        calmSince.current = null;
        return;
      }
      const now = Date.now();
      if (calmSince.current === null) calmSince.current = now;
      if (now - calmSince.current >= NUDGE_RULES.calmDwellMs) {
        calmSince.current = null;
        void decideAndShow();
      }
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [decideAndShow, enabled, userId]);

  const close = () => setNudge(null);
  const dismissProfile = (kind: 'photo' | 'verification') => {
    if (userId) void patchNudgeState(userId, profileDismissPatch(kind, stateRef.current, Date.now())).catch(() => undefined);
    close();
  };
  const surveyId = config?.surveyId || '';

  let modal: React.ReactNode = null;
  if (nudge?.kind === 'photo') {
    modal = (
      <TonyteModal
        visible
        title={t(`nudges.photo.v${nudge.variant}.title`)}
        message={t(`nudges.photo.v${nudge.variant}.body`)}
        icon="camera"
        iconColor={Colors.sunset}
        buttons={[
          { label: t('nudges.notNow'), variant: 'secondary', onPress: () => dismissProfile('photo') },
          { label: t('nudges.photo.cta'), variant: 'primary', onPress: () => { close(); if (navigationRef.isReady()) navigationRef.navigate('CompleteProfile', { initialStep: 'profile_photo' } as never); } },
        ]}
        onDismiss={() => dismissProfile('photo')}
      />
    );
  } else if (nudge?.kind === 'verification') {
    const key = nudge.mode === 'retry' ? `retry${nudge.variant}` : `v${nudge.variant}`;
    modal = (
      <TonyteModal
        visible
        title={t(`nudges.verification.${key}.title`)}
        message={t(`nudges.verification.${key}.body`)}
        icon="check-circle"
        iconColor={Colors.sunset}
        buttons={[
          { label: t('nudges.notNow'), variant: 'secondary', onPress: () => dismissProfile('verification') },
          {
            label: nudge.mode === 'retry' ? t('nudges.verification.retryCta') : t('nudges.verification.cta'),
            variant: 'primary',
            onPress: () => { close(); if (navigationRef.isReady()) navigationRef.navigate('VerificationCenter'); },
          },
        ]}
        onDismiss={() => dismissProfile('verification')}
      />
    );
  } else if (nudge?.kind === 'survey') {
    const remindLater = () => {
      if (userId) void patchNudgeState(userId, { surveys: { [surveyId]: surveyRemindLaterPatch(stateRef.current?.surveys?.[surveyId], Date.now()) } }).catch(() => undefined);
      close();
    };
    modal = (
      <TonyteModal
        visible
        title={t(`nudges.survey.v${nudge.variant}.title`)}
        message={t(`nudges.survey.v${nudge.variant}.body`)}
        icon="message-square"
        iconColor={Colors.turquoise}
        buttons={[
          { label: t('nudges.survey.never'), variant: 'secondary', onPress: () => { if (userId) void patchNudgeState(userId, { surveys: { [surveyId]: { dontAskAgain: true } } }).catch(() => undefined); close(); } },
          { label: t('nudges.survey.later'), variant: 'secondary', onPress: remindLater },
          { label: t('nudges.survey.cta'), variant: 'primary', onPress: () => { close(); setSurveyOpen({ manual: false }); } },
        ]}
        onDismiss={remindLater}
      />
    );
  }

  return (
    <>
      {modal}
      {/* Wait for the survey config so a cold start from the push doesn't flash "no survey running". */}
      {surveyOpen && userId && (surveyOpen.preview || config !== undefined) ? (
        <FeedbackSurveyModal
          visible
          uid={userId}
          // Dev preview: a stand-in active survey and a 30-day-old account; nothing is saved.
          config={surveyOpen.preview ? { active: true, surveyId: 'dev-preview' } : config ?? null}
          survey={surveyOpen.preview ? null : state?.surveys?.[surveyId] || null}
          firstUse={surveyOpen.preview ? Date.now() - 30 * 24 * 60 * 60 * 1000 : firstUse}
          manual={surveyOpen.manual}
          preview={surveyOpen.preview}
          onClose={() => setSurveyOpen(null)}
        />
      ) : null}
    </>
  );
};

export default OptionalNudgeHost;
